/**
 * Wave 4 stage K (analytics): the client event queue. Batches small events, sends at most a few
 * requests per minute (token bucket), never blocks (all work is O(1) per event; serialising
 * happens only at a flush), and hands the rest to `sendBeacon` when the page is hidden.
 *
 * Without consent a flush keeps only the anonymous count kinds and carries no id.
 */
import { ANALYTICS_VERSION, ANONYMOUS_KINDS, type AnalyticsBatch, type AnalyticsEvent } from "../../shared/analytics";

export interface AnalyticsTransport {
  /** Normal send (fetch, keepalive). Failures are ignored. */
  send(body: string): void;
  /** Page is going away: sendBeacon (returns false when the browser refused it). */
  beacon(body: string): boolean;
}

export interface AnalyticsIdentity {
  consent: boolean;
  pid: string | null;
}

export interface QueueOptions {
  transport: AnalyticsTransport;
  identity: () => AnalyticsIdentity;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  /** A flush happens this long after the first queued event (default 30 s). */
  flushDelayMs?: number;
  /** ... or as soon as this many events wait (default 25). */
  flushSize?: number;
  /** Events kept while waiting (oldest dropped beyond; default 300). */
  maxQueue?: number;
  /** Requests per minute (default 4) and burst (default 2). */
  requestsPerMin?: number;
  burst?: number;
  /** Events in one request (default 50; the server takes up to 60). */
  maxBatchEvents?: number;
}

const FEATURE_ID_RE = /[^A-Za-z0-9_.:+-]/g;

/** A feature id that passes the server's id rule (unknown characters become "_", max 48). */
export function featureId(raw: string): string {
  return raw.replace(FEATURE_ID_RE, "_").slice(0, 48) || "unknown";
}

export class AnalyticsQueue {
  private events: AnalyticsEvent[] = [];
  private readonly features = new Map<string, number>();
  private enabled: boolean | null = null;
  private timer: unknown = null;
  private tokens: number;
  private lastRefill: number;
  /** Normal flushes wait until then (the consent question is still open). */
  private heldUntil = 0;
  private readonly o: Required<Omit<QueueOptions, "transport" | "identity">> & Pick<QueueOptions, "transport" | "identity">;
  /** Counters for tests and the dev console. */
  readonly stats = { requests: 0, beacons: 0, sent: 0, dropped: 0 };

  constructor(options: QueueOptions) {
    this.o = {
      now: () => Date.now(),
      setTimer: (fn, ms) => setTimeout(fn, ms),
      clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
      flushDelayMs: 30_000,
      flushSize: 25,
      maxQueue: 300,
      requestsPerMin: 4,
      burst: 2,
      maxBatchEvents: 50,
      ...options,
    };
    this.tokens = this.o.burst;
    this.lastRefill = this.o.now();
  }

  /** null = waiting for the server's answer (events queue), false = off (everything dropped). */
  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) {
      this.stats.dropped += this.events.length;
      this.events = [];
      this.features.clear();
      this.cancelTimer();
    } else if (this.pending() > 0) this.schedule(this.o.flushDelayMs);
  }

  get isEnabled(): boolean | null {
    return this.enabled;
  }

  /** Events and feature counters waiting. */
  pending(): number {
    return this.events.length + this.features.size;
  }

  push(ev: AnalyticsEvent): void {
    if (this.enabled === false) return;
    this.events.push(ev);
    if (this.events.length > this.o.maxQueue) {
      this.events.shift();
      this.stats.dropped++;
    }
    if (this.enabled && this.events.length >= this.o.flushSize) this.flush();
    else this.schedule(this.o.flushDelayMs);
  }

  /** Count one use of a feature (summed until the next flush). */
  feature(id: string, n = 1): void {
    if (this.enabled === false) return;
    const key = featureId(id);
    if (!this.features.has(key) && this.features.size >= 200) return;
    this.features.set(key, Math.min(1000, (this.features.get(key) ?? 0) + n));
    this.schedule(this.o.flushDelayMs);
  }

  private schedule(ms: number): void {
    if (this.timer !== null || this.enabled !== true) return;
    this.timer = this.o.setTimer(() => {
      this.timer = null;
      this.flush();
    }, ms);
  }

  private cancelTimer(): void {
    if (this.timer !== null) this.o.clearTimer(this.timer);
    this.timer = null;
  }

  private takeToken(): boolean {
    const now = this.o.now();
    this.tokens = Math.min(this.o.burst, this.tokens + ((now - this.lastRefill) / 60_000) * this.o.requestsPerMin);
    this.lastRefill = now;
    if (this.tokens < 1) return false;
    this.tokens -= 1;
    return true;
  }

  /** Move the summed feature counters into the event list and apply the consent filter. */
  private collect(): AnalyticsEvent[] {
    for (const [f, n] of this.features) this.events.push({ e: "feature", f, n });
    this.features.clear();
    const id = this.o.identity();
    if (!id.consent || !id.pid) {
      const kept = this.events.filter((ev) => ANONYMOUS_KINDS.has(ev.e));
      this.stats.dropped += this.events.length - kept.length;
      this.events = kept;
    }
    return this.events;
  }

  private body(events: AnalyticsEvent[]): string {
    const id = this.o.identity();
    const batch: AnalyticsBatch = id.consent && id.pid ? { v: ANALYTICS_VERSION, consent: true, pid: id.pid, events } : { v: ANALYTICS_VERSION, consent: false, events };
    return JSON.stringify(batch);
  }

  /**
   * Hold normal flushes for `ms` (the first visit's consent question is open), so a "yes" given
   * within that time still sends the first-touch session with the anonymous id. Page-hide
   * beacons are never held.
   */
  hold(ms: number): void {
    this.heldUntil = this.o.now() + ms;
  }

  /** The question was answered: send what waits soon. */
  release(): void {
    if (this.heldUntil === 0) return;
    this.heldUntil = 0;
    this.cancelTimer();
    if (this.pending() > 0) this.schedule(1_000);
  }

  /** Send one batch now if the rate limit allows (otherwise retry later). */
  flush(): boolean {
    if (this.enabled !== true) return false;
    this.cancelTimer();
    const wait = this.heldUntil - this.o.now();
    if (wait > 0) {
      this.schedule(wait);
      return false;
    }
    const events = this.collect();
    if (events.length === 0) return false;
    if (!this.takeToken()) {
      this.schedule(Math.ceil(60_000 / this.o.requestsPerMin));
      return false;
    }
    const batch = events.splice(0, this.o.maxBatchEvents);
    this.o.transport.send(this.body(batch));
    this.stats.requests++;
    this.stats.sent += batch.length;
    if (events.length > 0) this.schedule(Math.ceil(60_000 / this.o.requestsPerMin));
    return true;
  }

  /**
   * The page is being hidden / closed: hand the waiting events to sendBeacon. Beacons count
   * against the request budget: the first always goes (the page may be gone after this), up to 2
   * more only while tokens are left; the rest stays queued for the next flush.
   */
  flushOnHide(): void {
    if (this.enabled !== true) return;
    this.cancelTimer();
    const events = this.collect();
    for (let i = 0; i < 3 && events.length > 0; i++) {
      if (!this.takeToken()) {
        if (i > 0) break;
        this.tokens = Math.max(-this.o.burst, this.tokens - 1); // the first beacon goes anyway, paid for by the next requests
      }
      const batch = events.splice(0, this.o.maxBatchEvents);
      if (!this.o.transport.beacon(this.body(batch))) this.o.transport.send(this.body(batch));
      this.stats.beacons++;
      this.stats.sent += batch.length;
    }
    if (events.length > 0) this.schedule(Math.ceil(60_000 / this.o.requestsPerMin));
  }
}
